import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Eye, Trash2, Filter, AlertCircle, CheckCircle, ArrowLeft, RefreshCw } from 'lucide-react';

const VehicleInspectionManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [filter, setFilter] = useState({ status: '', vehicle_id: '' });

  useEffect(() => {
    fetchInspections();
    fetchVehicles();
  }, [filter]);

  const fetchInspections = async () => {
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.vehicle_id) params.vehicle_id = filter.vehicle_id;
      
      const response = await axios.get(`${API}/vehicle-inspections`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to fetch inspections');
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Failed to fetch vehicles');
    }
  };

  const handleView = async (inspectionId) => {
    try {
      const response = await axios.get(`${API}/vehicle-inspections/${inspectionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInspection(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load inspection');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inspection?')) return;
    try {
      await axios.delete(`${API}/vehicle-inspections/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Inspection deleted');
      fetchInspections();
    } catch (error) {
      toast.error('Failed to delete inspection');
    }
  };

  const vehicleLabel = (v) => {
    if (!v) return 'Unknown';
    const num = v.veh_number || v.vehicle_number || '';
    const mm = [v.year, v.make, v.model].filter(Boolean).join(' ');
    const parts = [num && `#${num}`, mm, v.vin && `VIN ${v.vin}`, v.tag && `Tag ${v.tag}`].filter(Boolean);
    return parts.join(' · ') || 'Vehicle';
  };

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicleLabel(vehicle);
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Completed': 'bg-green-100 text-green-800',
      'Needs Follow-Up': 'bg-orange-100 text-orange-800'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  const countIssues = (inspection) => {
    let count = 0;
    const allItems = [
      ...inspection.exterior_items,
      ...inspection.interior_items,
      ...inspection.fluids_items,
      ...inspection.safety_equipment_items,
      ...inspection.mechanical_items
    ];
    allItems.forEach(item => {
      if (item.status === 'needs_attention') count++;
    });
    return count;
  };

  const renderInspectionItems = (title, items) => {
    if (!items || items.length === 0) return null;
    
    return (
      <div className="mb-4">
        <h4 className="font-semibold text-sm mb-2 text-slate-700">{title}</h4>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div 
              key={index} 
              className={`p-3 rounded border ${
                item.status === 'needs_attention' ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <p className="text-sm font-medium flex-1">{item.item}</p>
                {item.status === 'needs_attention' ? (
                  <AlertCircle className="h-4 w-4 text-orange-600 flex-shrink-0 ml-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 ml-2" />
                )}
              </div>
              {item.status === 'needs_attention' && item.description && (
                <p className="text-xs text-slate-600 mt-2 pl-2 border-l-2 border-orange-400">
                  {item.description}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center space-x-2 mb-4">
        <Button onClick={() => navigate('/admin')} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Vehicle Inspection Management</span>
            <Button onClick={fetchInspections} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </CardTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="text-xs">Filter by Status</Label>
              <select
                className="w-full border rounded-md px-3 py-2 mt-1"
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                data-testid="filter-status-select"
              >
                <option value="">All Statuses</option>
                <option value="Completed">Completed</option>
                <option value="Needs Follow-Up">Needs Follow-Up</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Filter by Vehicle</Label>
              <select
                className="w-full border rounded-md px-3 py-2 mt-1"
                value={filter.vehicle_id}
                onChange={(e) => setFilter({ ...filter, vehicle_id: e.target.value })}
                data-testid="filter-vehicle-select"
              >
                <option value="">All Vehicles</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicleLabel(vehicle)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {inspections.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No inspections found.</p>
            ) : (
              inspections.map((inspection) => {
                const issuesCount = countIssues(inspection);
                return (
                  <div 
                    key={inspection.id} 
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    style={{ borderLeft: `4px solid ${issuesCount > 0 ? '#F59E0B' : '#10B981'}` }}
                    data-testid="inspection-item"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-lg">{inspection.inspection_number}</h3>
                          {issuesCount > 0 && (
                            <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded-full">
                              {issuesCount} issue{issuesCount > 1 ? 's' : ''}
                            </span>
                          )}
                          {getStatusBadge(inspection.status)}
                        </div>
                        <p className="text-sm font-medium text-slate-700">
                          Vehicle: {getVehicleInfo(inspection.vehicle_id)}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">
                          Date: {_fmtDate(inspection.date_of_inspection)} • Location: {inspection.location_of_inspection}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Inspector: {inspection.driver_signature}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleView(inspection.id)}
                          data-testid="view-inspection-btn"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(inspection.id)}
                          data-testid="delete-inspection-btn"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inspection Details</DialogTitle>
          </DialogHeader>
          {selectedInspection && (
            <div className="space-y-6">
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Inspection Number</Label>
                    <p className="font-bold">{selectedInspection.inspection_number}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Vehicle</Label>
                    <p className="font-bold">{getVehicleInfo(selectedInspection.vehicle_id)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Date</Label>
                    <p className="font-medium">{_fmtDate(selectedInspection.date_of_inspection)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Location</Label>
                    <p className="font-medium">{selectedInspection.location_of_inspection}</p>
                  </div>
                </div>
              </div>

              {renderInspectionItems('EXTERIOR', selectedInspection.exterior_items)}
              {renderInspectionItems('INTERIOR', selectedInspection.interior_items)}
              {renderInspectionItems('FLUIDS', selectedInspection.fluids_items)}
              {renderInspectionItems('SAFETY EQUIPMENT', selectedInspection.safety_equipment_items)}
              {renderInspectionItems('MECHANICAL', selectedInspection.mechanical_items)}

              {selectedInspection.additional_notes && (
                <div>
                  <Label className="text-xs text-slate-500">Additional Notes</Label>
                  <p className="mt-1 p-3 bg-slate-50 rounded">{selectedInspection.additional_notes}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Driver Signature</Label>
                  <p className="font-medium italic">{selectedInspection.driver_signature}</p>
                </div>
                {selectedInspection.supervisor_signature && (
                  <div>
                    <Label className="text-xs text-slate-500">Supervisor Signature</Label>
                    <p className="font-medium italic">{selectedInspection.supervisor_signature}</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <div className="mt-1">{getStatusBadge(selectedInspection.status)}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehicleInspectionManagement;
